import { useCallback, useMemo } from 'react';
import { EngineService } from '@/services/engine';
import { DatabaseEvent } from '@/types/event';
import { PaginatedResponse } from '@/types/responses';
import { SortOption } from '@/utils/query-filters';
import {
    DatabaseEventsFilterParams,
    EVENTS_PAGE_SIZE,
    databaseEventsSortToOrderBy,
    useBaseEventFilters,
} from './eventsDataShared';

export interface UseDatabaseEventsQueryOptions {
    sourceId?: string;
    pageKey?: string;
    sortOption?: SortOption;
}

export interface DatabaseEventsQuery {
    cacheKey: string;
    pageStateKey?: string;
    filterParams: DatabaseEventsFilterParams;
    fetchPage: (pageIndex: number) => Promise<PaginatedResponse<DatabaseEvent>>;
}

export function useDatabaseEventsQuery({
    sourceId,
    pageKey,
    sortOption = SortOption.DATE_NEWEST,
}: UseDatabaseEventsQueryOptions = {}): DatabaseEventsQuery {
    const filterParams: DatabaseEventsFilterParams =
        useBaseEventFilters(sourceId);

    const orderBy = databaseEventsSortToOrderBy(sortOption);

    const cacheKey = useMemo(() => {
        const parts: string[] = ['database-events'];
        if (sourceId) parts.push(`source-${sourceId}`);
        parts.push(JSON.stringify(filterParams));
        parts.push(orderBy);
        return parts.join('-');
    }, [sourceId, filterParams, orderBy]);
    const pageStateKey = pageKey ? `${pageKey}:${cacheKey}` : undefined;

    const fetchPage = useCallback(
        (pageIndex: number): Promise<PaginatedResponse<DatabaseEvent>> =>
            EngineService.getDatabaseEvents({
                page: pageIndex + 1,
                size: EVENTS_PAGE_SIZE,
                orderBy,
                ...filterParams,
            }),
        [filterParams, orderBy],
    );

    return { cacheKey, pageStateKey, filterParams, fetchPage };
}
